import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { useAuthenticatedRootRedirect } from '@/hooks/useAuthenticatedRootRedirect';
import { useIsCompact } from '@/hooks/use-mobile';
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { HeroSection } from "@/components/home/HeroSection";
import { SubjectBrowseSection } from "@/components/home/SubjectBrowseSection";
import { SocialProofSection } from "@/components/home/SocialProofSection";
import { WhyBMCSection } from "@/components/home/WhyBMCSection";
import { HowItWorks } from "@/components/home/HowItWorks";
import { FAQSection } from "@/components/home/FAQSection";
import { PricingSection } from "@/components/home/PricingSection";
import { CTASection } from "@/components/home/CTASection";
import { MobileLanding } from "@/components/home/MobileLanding";

const Index = () => {
  const status = useAuthenticatedRootRedirect();
  const { setTheme } = useTheme();
  const { pathname } = useLocation();
  const isMobile = useIsCompact();

  // The landing page always uses the default (blue) theme, regardless of the
  // role-specific theme applied inside the dashboards.
  useEffect(() => {
    setTheme("light");
  }, [setTheme]);
  if (status === 'checking' || status === 'redirect') return null;

  const title = 'Find Verified Tutors & Coaching — BookMyCoaching';
  const description = 'Discover top tutors for CBSE, IGCSE, IIT-JEE and more. Browse verified coaches near you on BookMyCoaching.';
  const canonicalUrl = getCanonicalUrl(pathname);
  const ogImage = getDefaultOgImage();

  const helmet = (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
    </Helmet>
  );

  /* ── Mobile: app-shell experience ── */
  if (isMobile) {
    return (
      <>
        {helmet}
        <MobileLanding />
      </>
    );
  }

  /* ── Desktop: full marketing page ── */
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <HeroSection />
        <SubjectBrowseSection />
        <WhyBMCSection />
        <SocialProofSection />
        <HowItWorks />
        <PricingSection />
        <CTASection />
        <FAQSection />
      </main>
      <Footer />
    </div>
  );
};

export default Index;
